import { useMemo } from 'react'
import { useInfiniteQuery, useQueries } from '@tanstack/react-query'
import api from '../../config/axios-config'
import {
  planQueryKeys,
  plansApiTypeOptions,
  planTreeTypeFilterOptions
} from '../constants'

export const planTreeItemTypeOptions = {
  FOLDER: 'Folder',
  PLAN: 'Plan'
}

export const planTreeStateFilterOptions = {
  ALL: 'All',
  ACTIVE: 'Active',
  INACTIVE: 'Inactive'
}

export const planTreeSortByOptions = {
  TITLE: 'Title',
  LAST_ACTIVITY: 'LastActivity'
}

export const planTreeSortDirectionOptions = {
  ASC: 'ASC',
  DESC: 'DESC'
}

export const getPlanTreeChildrenQuery = async ({
  projectId,
  folderId = null,
  params = {},
  pageParam = 0
}) => {
  const {
    search = '',
    stateFilter = planTreeStateFilterOptions.ACTIVE,
    typeFilter = planTreeTypeFilterOptions.ALL,
    size = 50,
    sortBy = planTreeSortByOptions.TITLE,
    sortDirection = planTreeSortDirectionOptions.ASC
  } = params

  const type = folderId
    ? plansApiTypeOptions.FOLDERS
    : plansApiTypeOptions.PROJECTS

  const id = folderId ?? projectId

  const requestParams = {
    search,
    stateFilter,
    typeFilter,
    size,
    page: pageParam,
    sortBy,
    sortDirection
  }

  if (folderId) {
    delete requestParams.search
  }

  const config = {
    method: 'get',
    params: requestParams
  }

  const { data } = await api(`/api/plans-api/${type}/${id}/children`, config)

  return data
}

export const useGetPlanTreeQuery = ({
  projectId = null,
  folderId = null,
  params = {},
  options = {}
} = {}) => {
  const {
    search = '',
    stateFilter = planTreeStateFilterOptions.ACTIVE,
    typeFilter = planTreeTypeFilterOptions.ALL,
    size = 50,
    page = 0,
    sortBy = planTreeSortByOptions.TITLE,
    sortDirection = planTreeSortDirectionOptions.ASC
  } = params

  const handleQuery = async ({ pageParam = page }) => {
    return getPlanTreeChildrenQuery({
      projectId,
      folderId,
      params: {
        search,
        stateFilter,
        typeFilter,
        size,
        sortBy,
        sortDirection
      },
      pageParam
    })
  }

  return useInfiniteQuery({
    queryKey: [
      planQueryKeys.GET_PLANS_TREE_QUERY,
      projectId ?? null,
      folderId ?? null,
      search,
      stateFilter,
      typeFilter,
      size,
      page,
      sortBy,
      sortDirection
    ],
    queryFn: handleQuery,
    retry: 0,
    initialPageParam: page,
    getNextPageParam: (lastPage, _pages, lastPageParam) => {
      if (lastPage.last === true) {
        return undefined
      }
      return lastPageParam + 1
    },
    enabled: !!(projectId || folderId),
    ...options
  })
}

export const useGetPlanTreeChildrenQueries = ({
  projectId,
  folderIds = [],
  params = {},
  options = {}
} = {}) => {
  const {
    stateFilter = planTreeStateFilterOptions.ACTIVE,
    typeFilter = planTreeTypeFilterOptions.ALL,
    size = 50,
    sortBy = planTreeSortByOptions.TITLE,
    sortDirection = planTreeSortDirectionOptions.ASC
  } = params

  const folderChildrenQueries = useQueries({
    queries: folderIds.map((folderId) => ({
      queryKey: [
        planQueryKeys.GET_PLANS_CHILDREN_TREE_QUERY,
        'folderChildren',
        folderId,
        stateFilter,
        typeFilter,
        size,
        sortBy,
        sortDirection
      ],
      queryFn: () =>
        getPlanTreeChildrenQuery({
          projectId,
          folderId,
          params: {
            stateFilter,
            typeFilter,
            size,
            sortBy,
            sortDirection
          }
        }),
      retry: 0,
      enabled: !!folderId,
      ...options
    }))
  })

  const folderChildrenStateById = useMemo(() => {
    const childrenState = new Map()

    folderIds.forEach((folderId, index) => {
      childrenState.set(folderId, folderChildrenQueries[index])
    })

    return childrenState
  }, [folderIds, folderChildrenQueries])

  const childrenByFolderId = useMemo(() => {
    const children = new Map()

    folderChildrenStateById.forEach((query, folderId) => {
      if (query?.data?.content) {
        children.set(folderId, query.data.content)
      }
    })

    return children
  }, [folderChildrenStateById])

  return {
    folderChildrenQueries,
    folderChildrenStateById,
    childrenByFolderId
  }
}
